import { useState, useCallback, useEffect, useMemo, Fragment } from "react";
import { useQuery, useQueryClient } from "react-query";

import UserListPresenter from "./UserListPresenter";
import * as s from "../UsersStyled";
import TableButton from "components/atoms/buttons/TableButton";

import { getUserList, updateUser } from "api/userAPI";
import {
  BasePagination,
  CustomError,
  LEVEL_TYPE,
  PageTotalStatus,
  SORT_DIRECTION,
} from "api/interfaces/commonInterface.interface";
import { AddUserDto, UserDto } from "api/interfaces/userInterface.interface";
import { createUser } from "api/userAPI";
import { useAppSelector } from "redux/hooks";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";
import RBACWrapper from "components/blocks/function/RBACWrapper";
import { FaRegUser } from "react-icons/fa";
import { postResendUser } from "api/authAPI";
import { isCheckPermission } from "utils/AccountUtil";
import useApiError from "hook/useApiError";
import { useLocation, useOutletContext } from "react-router-dom";
import { isEmpty, isNull } from "lodash";
import { getAccountTimeFormat } from "utils/timeUtil";
import { TableColumnsType } from "antd";
import {
  changeSortOrderAntd,
  onSortAntd,
} from "components/atoms/table/AntdTable";
import { ColumnType } from "antd/es/table";
import { Default, useIsMobile } from "components/MediaQuery";
import { baseQueryInfo, useQueryKey } from "utils/queryUtil";
import { useMenu } from "hook/useMenu";
import * as MediaQuery from "components/MediaQuery";
import { UserPageOutletContext } from "../UserPage";
import useStatusMutation from "hook/useStatusMutation";
import { WithTooltip } from "components/blocks/WithTooltip";
import { MdAccessTimeFilled } from "react-icons/md";
import useStompDebouncedRefetch, {
  StompDateSignalKey,
} from "hook/useStompDebouncedRefetch";
import { checkEmpty } from "utils/functions";

const initialQueryInfo: BasePagination = baseQueryInfo;

export default function UserListContainer(): JSX.Element {
  const location = useLocation();
  const {
    setStatus: setUserPageStatus,
    setButtonLabel,
    setTitleComponent,
    setOnClickBtn,
    setOnClickSearch,
    setKeyword,
  } = useOutletContext<UserPageOutletContext>();
  const isBigScreen = MediaQuery.useIsBigScreen();
  const { updateCurrentAccount } = useMenu({});

  const queryClient = useQueryClient();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  const { handleErrorNotify, notifyError, lang, notifySuccess } = useApiError();

  const [isAddModal, setIsAddModal] = useState<boolean>(false);
  const [isEditModal, setIsEditModal] = useState<boolean>(false);

  const [status, setStatus] = useState<PageTotalStatus>({
    totalTitle: "Total Users",
    totalValue: 0,
  });

  const { queryNavigate, returnToStartPage } = useMenu({});
  useEffect(() => {
    if (!selectedAccount.userPermission.viewUsersAndGroups) {
      returnToStartPage();
    }
  }, [selectedAccount.userPermission.viewUsersAndGroups]);

  useEffect(() => {
    const changeParam = new URLSearchParams(location.search);
    if (changeParam.size > 0) {
      setUserQueryInfo((info) => {
        return {
          ...info,
          pageNum: isNull(changeParam.get("pageNum"))
            ? 0
            : Number(changeParam.get("pageNum")),
          pageLimit: isNull(changeParam.get("pageLimit"))
            ? 20
            : Number(changeParam.get("pageLimit")),
          keyword: isNull(changeParam.get("keyword"))
            ? ""
            : (changeParam.get("keyword") as string),
          sortType: isNull(changeParam.get("sortType"))
            ? "name"
            : (changeParam.get("sortType") as string),
          sortDirection: isNull(changeParam.get("sortDirection"))
            ? SORT_DIRECTION.ASC
            : (changeParam.get("sortDirection") as SORT_DIRECTION),
        };
      });
    } else {
      setUserQueryInfo({ ...initialQueryInfo });
    }
  }, [location]);

  const [totalUsers, setTotalUsers] = useState<UserDto[]>([]);
  const [currUser, setCurrUser] = useState<UserDto>();
  const [pending, setPending] = useState<boolean>(true);

  const [userQueryInfo, setUserQueryInfo] = useState<BasePagination>({
    ...initialQueryInfo,
  });
  const queryKey = useQueryKey(userQueryInfo);
  const isMobile = useIsMobile();

  const onHeaderCell = (column: ColumnType<UserDto>) => {
    return {
      onClick: () => {
        if (column.key) {
          setUserQueryInfo((info) => {
            return {
              ...info,
              sortType: column.key as string,
              sortDirection: onSortAntd(info.sortDirection),
            };
          });
        }
      },
    };
  };

  // user roles
  const columns: TableColumnsType<UserDto> = [
    {
      key: "name",
      title: "Name",
      dataIndex: "name",
      render: (value, row) => {
        const isAdmin = row.userGroups?.some(
          (ug) => ug.name === "Administrator"
        );

        return (
          <>
            <s.Row isAdmin={isAdmin}>
              <Default>
                <div>
                  <FaRegUser size={16} />
                </div>
              </Default>
              {row.isActivate || !checkEmpty(row.name) ? row.name : "Invited User"}
              {row.isTemporaryAccess && (
                <WithTooltip
                  tooltip={getAccountTimeFormat(
                    row.temporaryAccessExpiredAt,
                    selectedAccount
                  )}
                >
                  <s.TemporaryUser>
                    <MdAccessTimeFilled size={12} />
                    TEMPORARY
                  </s.TemporaryUser>
                </WithTooltip>
              )}
            </s.Row>
            {row.email && <s.Row ellipsis>{row.email}</s.Row>}
          </>
        );
      },
      sorter: true,
      sortOrder: changeSortOrderAntd(userQueryInfo, "name"),
      onHeaderCell: onHeaderCell,
      defaultSortOrder: "ascend",
    },
    {
      title: "Group",
      render: (value, row) => (
        <s.UserGroups>
          {row.userGroups?.map((group, index) => (
            <span key={index}>
              {index > 0 && " "}
              {group.name}
              {index < (row.userGroups ?? []).length - 1 && ","}
            </span>
          ))}
        </s.UserGroups>
      ),

      width: "20%",
    },
    {
      title: "Invited",
      render: (value, row) => (
        <>
          <s.Row>
            {row.invitedDate !== undefined
              ? getAccountTimeFormat(row.invitedDate, selectedAccount, true)
              : ""}
          </s.Row>
          <s.Row>
            {row.invitedEmail !== undefined
              ? `${row.invitedBy} ${isBigScreen ? `(${row.invitedEmail})` : ""}`
              : row.isActivate
              ? "Organization Creator"
              : ""}
          </s.Row>
        </>
      ),

      responsive: ["md"],
      width: "15%",
      // sorter: true,
      // sortField: "invitedDate",
    },
    {
      title: "Restricted Recorders",
      render: (value, row) => {
        if ((row.restrictList || []).length > 0) {
          const name = row.restrictList[0].mergedSystemName;
          if (row.restrictList.length === 1) {
            return name;
          }
          return (
            <WithTooltip
              tooltip={row.restrictList.map((item, index) => (
                <Fragment key={index}>
                  {index > 0 && <br />}
                  {item.mergedSystemName}
                </Fragment>
              ))}
              style={{ fontSize: "12px" }}
            >
              <>
                {name}{" "}
                {row.restrictList.length > 1 &&
                  `+ ${row.restrictList.length - 1}`}
              </>
            </WithTooltip>
          );
        }
        return "";
      },
      responsive: ["lg"],
      width: "10%",
      hidden: selectedAccount.accountLevel !== LEVEL_TYPE.EU,
    },
    {
      title: "Description",
      render: (value, row) => row.description,
      sorter: true,
      sortOrder: changeSortOrderAntd(userQueryInfo, "description"),
      onHeaderCell: onHeaderCell,
      key: "description",
      ellipsis: true,
      responsive: ["xxl"],
      width: 250,
    },
    {
      title: "",
      width: isMobile ? 80 : 180,
      render: (value, row, index) => (
        <s.Row className="table-button-wrapper">
          <RBACWrapper requiredPermissions="manageUsersAndGroups">
            {!row.isActivate && (
              <TableButton
                label="Resend"
                buttonType="view"
                onClick={(e) => {
                  e.stopPropagation();
                  onResend(row);
                }}
                data-testid={`users-user-list-table-button-resend-${index}`}
              />
            )}

            <span className="row-click-button">
              <TableButton
                label="Edit"
                onClick={() => onRowClicked(row)}
                data-testid={`users-user-list-table-button-edit-${index}`}
              />
            </span>
          </RBACWrapper>
        </s.Row>
      ),

      align: "right",
    },
  ];

  const mutationResendUser = useStatusMutation(postResendUser, {
    onSuccess: () => {
      notifySuccess(lang.label.user.notify.resend.success);
    },
    onError: () => {
      notifyError(lang.label.user.notify.resend.fail);
    },
  });

  const onResend = (row: UserDto) => {
    mutationResendUser.mutate({
      accountId: selectedAccount.accountId,
      userId: row.userId,
    });
  };

  const onRowClicked = (row: UserDto) => {
    if (isCheckPermission("manageUsersAndGroups", selectedAccount)) {
      const selectUser = Array.from(totalUsers).find(
        (user) => user.userId === row.userId
      );
      setCurrUser(selectUser);
      onModal("edit");
    }
  };

  const onSuccessDelete = () => {
    setIsEditModal(false);
    refetch();
    queryNavigate({
      to: location.pathname,
      queryInfo: {
        ...initialQueryInfo,
      },
    });
  };

  const onModal = useCallback(
    (type?: string) => {
      if (type === "add") {
        if (isAddModal) {
          queryClient.invalidateQueries("user");
        }
        setIsAddModal(!isAddModal);
      }
      if (type === "edit") {
        if (isEditModal) {
          queryClient.invalidateQueries("user");
        }
        setIsEditModal(!isEditModal);
      }
    },
    [isAddModal, isEditModal, queryClient]
  );

  const { refetch, data } = useQuery(
    ["user", queryKey, selectedAccount.accountId],
    () =>
      getUserList({
        payload: userQueryInfo,
        accountId: selectedAccount.accountId,
      }),
    {
      retry: 0,
      refetchOnWindowFocus: false,
      onSuccess: (res: any) => {
        if (res.error !== 0 || res.result === undefined) {
          setUserQueryInfo((info) => {
            return { ...initialQueryInfo };
          });
          setStatus((info) => {
            return { ...info, totalValue: 0 };
          });
          return;
        }

        setUserQueryInfo((info) => {
          return { ...info, ...res.page };
        });

        setTotalUsers(res.result);

        setStatus({
          totalTitle: "Total Users",
          totalValue: res.page.total,
        });
      },
      onError: (e: any) => {
        handleErrorNotify(e, lang.label.user.notify.list.fail);
        setStatus((info) => {
          return { ...info, totalValue: 0 };
        });
      },
      onSettled: (e: any) => {
        setPending(false);
      },
    }
  );
  useStompDebouncedRefetch({
    stompKey: ["userListUpdated"] as StompDateSignalKey[],
    refetch: refetch,
  });

  const mutationCreateUser = useStatusMutation(createUser, {
    onSuccess: () => {
      notifySuccess(lang.label.user.notify.add.success);
      queryClient.invalidateQueries("user");
      setIsAddModal(false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.user.notify.add.fail);
    },
  });

  const mutationUpdateUser = useStatusMutation(updateUser, {
    onSuccess: () => {
      notifySuccess(lang.label.user.notify.update.success);
      updateCurrentAccount();
      queryClient.invalidateQueries("user");
      setIsEditModal(false);
    },
    onError: (err: CustomError) => {
      handleErrorNotify(err, lang.label.user.notify.update.fail);
    },
  });

  const onClickSave = useCallback(
    (user: UserDto | AddUserDto, update?: boolean) => {
      if (!mutationCreateUser.idle || !mutationUpdateUser.idle) return;
      if (update) {
        mutationUpdateUser.mutate({
          accountId: selectedAccount.accountId,
          userId: user.userId,
          payload: user as AddUserDto,
        });
      } else {
        mutationCreateUser.mutate({
          accountId: selectedAccount.accountId,
          payload: user,
        });
      }
    },
    [mutationCreateUser, mutationUpdateUser, selectedAccount.accountId]
  );

  const onChangePage = useCallback(
    (page: number, totalRows: number) => {
      console.log(page, totalRows);
      if (page !== userQueryInfo.pageNum + 1) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: userQueryInfo.pageLimit,
            pageNum: page - 1,
            keyword: userQueryInfo.keyword as string,
            sortType: userQueryInfo.sortType,
            sortDirection: userQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
        setPending(true);
        // setUserQueryInfo((info) => {
        //   return { ...info, pageNum: (page - 1) as number };
        // });
      }
    },
    [
      location.pathname,
      userQueryInfo.keyword,
      userQueryInfo.pageLimit,
      userQueryInfo.pageNum,
      userQueryInfo.sortDirection,
      userQueryInfo.sortType,
      queryNavigate,
    ]
  );

  const onClickSearch = useCallback(
    (keyword: string) => {
      if (userQueryInfo.keyword !== keyword) {
        queryNavigate({
          to: location.pathname,
          queryInfo: {
            pageLimit: userQueryInfo.pageLimit,
            pageNum: 0,
            keyword: keyword,
            sortType: userQueryInfo.sortType,
            sortDirection: userQueryInfo.sortDirection as SORT_DIRECTION,
          },
        });
      } else {
        refetch();
      }
      setPending(true);
      // setUserQueryInfo((query) => {
      //   return {
      //     ...query,
      //     keyword,
      //   };
      // });
      // refetch();
    },
    [
      location.pathname,
      refetch,
      userQueryInfo.keyword,
      userQueryInfo.pageLimit,
      userQueryInfo.sortDirection,
      userQueryInfo.sortType,
      queryNavigate,
    ]
  );

  useEffect(() => {
    setTitleComponent("User List");
    setButtonLabel("Add User");
    setOnClickBtn(() => () => onModal("add"));

    return () => {
      setOnClickBtn(undefined);
    };
  }, [onModal, setButtonLabel, setOnClickBtn, setTitleComponent]);

  useEffect(() => {
    setOnClickSearch(() => onClickSearch);
  }, [onClickSearch, setOnClickSearch]);

  useEffect(() => {
    setUserPageStatus(status);
  }, [setUserPageStatus, status]);

  useEffect(() => {
    setKeyword((userQueryInfo.keyword as string) ?? "");
  }, [setKeyword, userQueryInfo.keyword]);

  const users: UserDto[] = useMemo(() => {
    if (data?.result && data.error === 0) {
      return data.result;
    }
    return [];
  }, [data]);

  return (
    <UserListPresenter
      columns={columns}
      data={users}
      selectedUser={currUser}
      onModal={onModal}
      isAddModal={isAddModal}
      isEditModal={isEditModal}
      status={status}
      onClickSave={onClickSave}
      onChangePage={onChangePage}
      onClickSearch={onClickSearch}
      initialPending={pending}
      paginationInfo={userQueryInfo}
      isCreateLoading={mutationCreateUser.isLoading}
      isUpdateLoading={mutationUpdateUser.isLoading}
      onSuccessDelete={onSuccessDelete}
    />
  );
}
